import React, { useState, useEffect } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { CheckCircle } from 'react-feather';
import { Link } from 'react-router-dom';
import DashboardLayout from '../common/DashboardLayout';
import PaymentPageForm from './card';
import AdminServices from '../../services/AdminServices';

/**
 * Renewal checkout for school admins.
 *
 * Loads the school's own subscription status (GET /admin/my-subscription) to get the
 * renewal amount (their current rate) and renders the existing checkout form, which
 * charges and sets up the subscription (annual auto-renew) on success. Reached from
 * the blinking "Renew" nav item, which only appears for non-auto-renewing schools.
 */
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch (e) { return ''; }
};

const RenewCheckout = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = Number(localStorage.getItem('userrole') || 0);

  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await AdminServices.getMySubscription();
      if (!cancelled) { setSub(s); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const renderBody = () => {
    if (loading) {
      return <div className="text-center p-5"><Spinner animation="border" role="status" /></div>;
    }
    if (done) {
      return (
        <Alert variant="success" className="d-flex align-items-center">
          <CheckCircle size={18} className="mr-2" />
          <span>Your subscription has been renewed. <Link to="/main-dashboard">Go to your dashboard</Link>.</span>
        </Alert>
      );
    }
    // A failed request is not the same as having no subscription, and telling a
    // paying school its subscription does not exist because a token expired is
    // the kind of message that generates a support call.
    if (sub && sub.error) {
      return (
        <Alert variant="warning">
          {sub.status === 401 || sub.status === 403
            ? <>Your session has expired. Please <Link to="/login">sign in again</Link> and reopen this page.</>
            : <>We couldn&apos;t load your subscription just now. Please refresh the page, or contact support if it keeps happening.</>}
        </Alert>
      );
    }
    if (!sub || !sub.hasSubscription) {
      return <Alert variant="info">We couldn&apos;t find an active subscription on your account. Please contact support to renew.</Alert>;
    }
    if (sub.isAutoRenew) {
      return (
        <Alert variant="success">
          Your subscription is set to <strong>auto-renew</strong>
          {sub.expirationDate ? ` on ${fmtDate(sub.expirationDate)}` : ''} — no action needed.
          Your card on file will be charged automatically. To change the card, use
          {' '}<Link to="/update-payment-info">Account &rarr; Subscription</Link>.
        </Alert>
      );
    }
    return (
      <>
        <p className="text-muted mb-4">
          {sub.expirationDate ? `Your subscription expires ${fmtDate(sub.expirationDate)}. ` : ''}
          Renew below to keep your school&apos;s training active. Renewing saves your card and sets up
          automatic yearly renewal so you&apos;re never interrupted again.
        </p>
        <PaymentPageForm
          amount={sub.renewalAmount}
          totalAmount={sub.renewalAmount}
          reemcode={null}
          pageType="renewal"
          onSuccess={() => setDone(true)}
        />
      </>
    );
  };

  return (
    <DashboardLayout role={userRole} userType={user?.type} activeMenu="Renew" layoutType="topnav">
      <div className="container-fluid p-4">
        <h2 className="mb-4">Renew Your Subscription</h2>
        {renderBody()}
      </div>
    </DashboardLayout>
  );
};

export default RenewCheckout;
